<script setup lang="ts">
import type { PositionCellValue, PositionRow } from '@/components/finest-custom-position-list/types'
import FinestMasterDetailCard from '@/components/FinestMasterDetailCard.vue'
import type { EnumOption } from '@/composables/useEnumOptions'
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import LoanEditor from './loans/LoanEditor.vue'
import LoanList, { type LoanListEntry } from './loans/LoanList.vue'
import { cellNumber, formatMoney } from './loans/loanFigures'
import CollateralData = App.Data.EstateObject.CollateralData

const props = withDefaults(
  defineProps<{
    title: string
    estateObjectId: string
    loanTypeOptions: EnumOption[]
    mezzanineTypeOptions: EnumOption[]
    collateralTypeOptions: EnumOption[]
    collaterals: Record<string, CollateralData[]>
    ensureSaved: (key: string) => Promise<string>
    addLabel?: string
  }>(),
  {
    addLabel: undefined,
  },
)

const rows = defineModel<PositionRow[]>('rows', { default: () => [] })

const emit = defineEmits<{
  collateralsChange: [loanKey: string, collaterals: CollateralData[]]
  addRow: []
  removeRow: [key: string]
  commit: []
}>()

const { t, locale } = useI18n({ useScope: 'local' })

const selected = ref<string>(rows.value[0]?.key ?? '')

const selectedRow = computed(() => rows.value.find((row) => row.key === selected.value) ?? null)

const countLabel = computed(() => `${rows.value.length} ${t('position', rows.value.length)}`)

const typeLabel = (row: PositionRow): string =>
  props.loanTypeOptions.find((option) => option.value === row.cells.loan_type)?.label ?? ''

const rowTitle = (row: PositionRow): string => {
  const lender = row.cells.lender_name

  return typeof lender === 'string' && lender.trim() !== '' ? lender : typeLabel(row) || t('unnamed')
}

const entries = computed<LoanListEntry[]>(() =>
  rows.value.map((row) => ({
    key: row.key,
    title: rowTitle(row),
    subtitle: formatMoney(cellNumber(row, 'principal_amount'), locale.value),
  })),
)

const detail = useTemplateRef<HTMLElement>('detail')

const REVEALED_RATIO = 0.75

async function revealDetail(): Promise<void> {
  await nextTick()

  const element = detail.value

  if (!element) {
    return
  }

  const { top, bottom, height } = element.getBoundingClientRect()
  const visible = Math.min(bottom, window.innerHeight) - Math.max(top, 0)
  const revealable = Math.min(height, window.innerHeight * REVEALED_RATIO)

  if (visible < revealable) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

function select(key: string): void {
  selected.value = key
  void revealDetail()
}

function changeCollaterals(list: CollateralData[]): void {
  const loanId = selectedRow.value?.id

  if (loanId) {
    emit('collateralsChange', loanId, list)
  }
}

function setCell(rowKey: string, columnKey: string, value: PositionCellValue): void {
  rows.value = rows.value.map((row) =>
    row.key === rowKey ? { ...row, cells: { ...row.cells, [columnKey]: value } } : row,
  )
}

watch(
  () => rows.value.map((row) => row.key),
  (keys, previousKeys) => {
    const added = keys.find((key) => !previousKeys?.includes(key))

    if (added && keys.length > (previousKeys?.length ?? 0)) {
      select(added)
    } else if (!keys.includes(selected.value)) {
      selected.value = keys[0] ?? ''
    }
  },
)
</script>

<template>
  <FinestMasterDetailCard>
    <template #header>
      <div class="mb-5 flex items-center justify-between gap-3">
        <h2 class="text-xl font-bold">{{ title }}</h2>
        <span class="bg-surface-3 text-ink-2 rounded-full px-2.5 py-1 text-sm font-medium">{{ countLabel }}</span>
      </div>
    </template>

    <template #master>
      <LoanList :entries="entries" :selected="selected" :add-label="addLabel" @select="select" @add="emit('addRow')" />
    </template>

    <template #detail>
      <div ref="detail" class="scroll-mt-20">
        <p v-if="!rows.length" class="text-ink-3 py-12 text-center text-sm">{{ t('empty') }}</p>

        <LoanEditor
          v-else-if="selectedRow"
          :row="selectedRow"
          :estate-object-id="estateObjectId"
          :loan-type-options="loanTypeOptions"
          :mezzanine-type-options="mezzanineTypeOptions"
          :collateral-type-options="collateralTypeOptions"
          :collaterals="collaterals[selectedRow.id ?? ''] ?? []"
          :ensure-loan-id="() => ensureSaved(selectedRow!.key)"
          @update="(columnKey, value) => setCell(selectedRow!.key, columnKey, value)"
          @collaterals-change="changeCollaterals"
          @remove="emit('removeRow', selectedRow!.key)"
          @commit="emit('commit')" />
      </div>
    </template>
  </FinestMasterDetailCard>
</template>

<i18n lang="json">
{
  "de": {
    "unnamed": "Finanzierung",
    "position": "Position | Positionen",
    "empty": "Noch keine Finanzierung – füge eine hinzu."
  },
  "en": {
    "unnamed": "Financing",
    "position": "position | positions",
    "empty": "No financing yet – add one."
  }
}
</i18n>
